'use client';

import React, { useState, Suspense, useMemo, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import {
  LocaleDetails,
  countryLanguages,
  Locale,
  getAvailableRegions,
  isRegionAvailable,
  getDefaultLocaleForRegion,
} from 'shared/src/types/languages.type';
import {
  Region,
  RegionCode,
  getMapRegionName,
  getRegionDisplayName,
  getRegionForCountryCode,
  getRegionGeoNames,
} from 'shared/src/types/region.type';
import {
  ALL_REGIONS_SENTINEL,
  getSubregion,
  getSubregionsForCountry,
  isWholeCountry,
} from 'shared/src/region/admin1-registry';
import { getCountryMapPreset } from '@/lib/country-map-presets';
import { CountryMapView, prefetchCountryMap } from '@/components/region/country-map-view';
import { SubdivisionPanel } from '@/components/region/subdivision-panel';
import { getRegionMapUiContent } from '@/lib/ui-content/region-map-ui-content';
import { useRegionUiContent } from '@/components/ui-content-provider';
import { RegionAvailabilityStatus } from '@/services/validate-region-availability';
import { useToast } from '@/hooks/use-toast';
import { ToastContainer } from '@/components/ui/toast';
import { InteractiveRegionGlobe } from '@/components/globe-3d/interactive-region-globe';
import { TutorialSpotlight } from '@/components/tutorial/tutorial-spotlight';
import { TutorialWelcome } from '@/components/tutorial/tutorial-welcome';
import { getTutorialUiContent } from '@/lib/ui-content/tutorial-ui-content';
import { LocationSearchPanel, PanelResolvedPlace } from '@/components/region/location-search-panel';
import { findRegionCodeByName, getRegionFullName as getRegionMapping } from '@/lib/region-names';
import {
  commitRegionSelection as commitSelection,
  type RegionSelection,
  type RegionSelectionSource,
} from '@/lib/region-selection';
import { subdivisionListSurface } from '@/lib/region-view-surface';
import { REGION_NAME_TO_CODE, countryCodeForAnyName, toRegionName } from '@/lib/region-country-lookup';
import { SubregionPromptDialog } from '@/components/region/subregion-prompt-dialog';
import { localizedCountryName } from '@/hooks/use-current-shopping-region';
import {
  applySubregionPromptChoice,
  markSubregionPromptAnswered,
  resolveSubregionPromptTarget,
  useSubregionEligibility,
  type SubregionFeature,
  type SubregionPromptChoice,
} from '@/lib/hooks/use-subregion-eligibility';

type Country = {
  id: string;
  name: string;
};

/* ── Epic 3: globe → animated 2D country map ──────────────────────────────
 * REGION_NAME_TO_CODE and countryCodeForAnyName live in lib/region-country-lookup
 * so the name resolution can be tested — the globe speaks world-atlas spellings
 * and the flag tiles speak Region enum names, and getting that wrong drops the
 * visitor into a whole-country commit with no state picker.
 */

/** One country, in every vocabulary the page needs to speak at once. */
type CountryTarget = {
  /** Region enum value — "Canada", "USA", "UK". */
  regionName: string;
  /** Internal country code — "CA", "US", "UK" (never "GB"). */
  countryCode: string;
  /** world-atlas spelling the globe indexed — "United States of America". */
  globeName: string;
  /** Human label for headings and aria text. */
  displayName: string;
};

/** A Places / detect resolution waiting for the visitor to confirm it. */
type PendingPlace = {
  city: string | null;
  /** Internal subregion code the place resolved to, when it resolved to one. */
  subregionCode: string | null;
  lat: number | null;
  lng: number | null;
};

/**
 * A selection on its way to a commit. `RegionSelection` carries everything the
 * shared resolver needs except the Region enum name, which this page already
 * holds and the resolver would otherwise re-derive.
 */
type PendingSelection = { regionName: string; selection: RegionSelection };

/** A selection parked behind the scope dialog, waiting for an answer. */
type SubregionPrompt = { pending: PendingSelection; feature: SubregionFeature };

/**
 * Surfaces deliberate enough to be worth a question. The globe and the flag
 * tiles are excluded: they are country-level acts, and there is no province
 * pick in them to confirm.
 *
 * Tutorial selections are NOT filtered here — since Epic 10 the tour reaches the
 * map and produces genuine `map-click` selections. They are suppressed one level
 * up, in `isSubregionPromptCandidate`, for a reason that belongs there.
 */
const SUBREGION_PROMPT_SOURCES: ReadonlySet<RegionSelectionSource> = new Set<RegionSelectionSource>([
  'map-click',
  'places',
  'detect-chip',
]);

/**
 * globe → flying → country → closing → globe.
 *
 * `flying` and `country` are separate states on purpose: the right panel and
 * the geometry prefetch start the moment the camera leaves, but the map itself
 * may only mount once the camera has actually arrived, or the scale-down
 * entrance has nothing to continue from.
 */
type RegionView =
  | { mode: 'globe' }
  | { mode: 'flying'; country: CountryTarget; preselect: string | null; pending: PendingPlace | null }
  | { mode: 'country'; country: CountryTarget; preselect: string | null; pending: PendingPlace | null }
  | { mode: 'closing'; country: CountryTarget };

/** Countries whose admin-1 geometry AND registry entries both exist. */
function hasCountryMap(countryCode: string): boolean {
  return !!getCountryMapPreset(countryCode) && getSubregionsForCountry(countryCode).length > 0;
}

/** SSR-safe media query. Starts false so the server and first client paint agree. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const read = () => setMatches(list.matches);
    read();
    list.addEventListener('change', read);
    return () => list.removeEventListener('change', read);
  }, [query]);
  return matches;
}

/** Tailwind's `md` is 768px here (only `sm` is overridden, to 840px). */
const MOBILE_QUERY = '(max-width: 767px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * How long the close-focus restore waits for the opening control to come back.
 *
 * The list panel crossfades in over 0.18s behind `AnimatePresence mode="wait"`,
 * so the country tile that opened the map is absent for roughly a dozen frames
 * after the map closes. ~40 frames (≈0.65s at 60Hz) clears that comfortably and
 * still gives up quickly enough that the fallback feels immediate.
 */
const FOCUS_RESTORE_FRAMES = 40;
/** Matches the globe overlay crossfade; also the mobile takeover's fade. */
const MAP_TRANSITION = { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const };

type MapViewerProps = {
  locale: LocaleDetails;
  returnUrl?: string;
  regionAvailability: RegionAvailabilityStatus[];
  tutorialMode?: boolean;
  searchQuery?: string;
};

const MapViewer = ({ locale, returnUrl, regionAvailability, tutorialMode = false, searchQuery: initialSearchQuery }: MapViewerProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const [showTutorialWelcome, setShowTutorialWelcome] = useState(tutorialMode);
  const [showTutorialSpotlight, setShowTutorialSpotlight] = useState(false);
  const [spotlightStep, setSpotlightStep] = useState(0);

  const router = useRouter();
  const uiContent = useRegionUiContent();
  const tutorialContent = getTutorialUiContent(locale);
  const { toasts, removeToast, showComingSoon } = useToast();

  // Country code -> region comes from shared/src/types/region.type so this page
  // cannot drift out of sync with the rest of the app again (the hand-maintained
  // copies that used to live here had lost Japan, which rendered it grey and
  // unselectable on the globe).
  //
  // Region enum VALUES are an internal vocabulary that merely happens to be
  // spelled in English ("Italy", "Japan", "New Zealand"). Rendering them raw put
  // English country names inside French chrome on /fr-CA/region. `Intl`
  // (through `localizedCountryName`, which also maps our internal UK to ISO GB)
  // is the only source that covers all 24 UI languages without a hand-kept
  // table, and it is what the header badge and the easter-egg dialog already
  // speak — so this is now one vocabulary, not three.
  //
  // DISPLAY ONLY: every lookup still keys on the Region enum name, and nothing
  // localized here is ever compared, stored as a code, or put in a URL.
  //
  // `useCallback` is load-bearing, not decoration: this is a prop of
  // LocationSearchPanel, whose `orderedAvailable` sort memo lists it as a
  // dependency. An unstable identity would re-sort the country grid on every
  // render.
  const getFullRegionName = useCallback(
    (regionName: string): string => {
      const code = REGION_NAME_TO_CODE[regionName];
      // Regions the parallel-enum join cannot resolve (only `All` today) keep
      // the shared English spelling rather than rendering a bare code.
      return code
        ? localizedCountryName(code, locale.languageCode)
        : getRegionDisplayName(regionName);
    },
    [locale.languageCode],
  );

  /**
   * The globe speaks world-atlas spellings ("United States of America") and
   * hands them to the hover tooltip verbatim. Route them through the same
   * localizer; a country we do not serve has no code to look up, so it keeps
   * the atlas spelling.
   */
  const localizedGeoName = useCallback(
    (geoName: string): string => {
      const region = getMapRegionName(geoName);
      return region ? getFullRegionName(region) : geoName;
    },
    [getFullRegionName],
  );

  // Define spotlight tour targets for region page
  const spotlightTargets = useMemo(() => {
    const targets = [
      {
        selector: '#region-globe-container',
        title: tutorialContent.regionSpotlight.globe.title,
        description: tutorialContent.regionSpotlight.globe.description,
        position: 'right' as const,
        padding: 20,
      },
      {
        selector: '#region-list-panel',
        title: tutorialContent.regionSpotlight.regionList.title,
        description: tutorialContent.regionSpotlight.regionList.description,
        position: 'left' as const,
        padding: 12,
      },
    ];

    // Add detected region spotlight if we detected the user's region
    if (detectedRegion) {
      targets.push({
        selector: `#region-button-${detectedRegion.replace(/\s+/g, '-').toLowerCase()}`,
        title: tutorialContent.regionSpotlight.yourRegion.title,
        description: tutorialContent.regionSpotlight.yourRegion.description,
        position: 'left' as const,
        padding: 8,
      });
    }

    return targets;
  }, [tutorialContent, detectedRegion]);

  // Detect user's region in tutorial mode
  useEffect(() => {
    if (!tutorialMode) return;

    const detectUserRegion = async () => {
      try {
        const response = await fetch('/api/detect-region');
        if (response.ok) {
          const data = await response.json();
          const detectedCountryCode = data.country as string;
          const regionName = getRegionForCountryCode(detectedCountryCode);
          if (regionName && getRegionStatus(regionName) === 'available') {
            setDetectedRegion(regionName);
          }
        }
      } catch (error) {
        console.error('Failed to detect user region:', error);
      }
    };

    detectUserRegion();
  }, [tutorialMode]);

  // Handle welcome start - transition to spotlight tour
  const handleWelcomeStart = () => {
    setShowTutorialWelcome(false);
    setShowTutorialSpotlight(true);
  };

  // Handle tutorial skip (from welcome or spotlight)
  const handleTutorialSkip = () => {
    setShowTutorialWelcome(false);
    setShowTutorialSpotlight(false);
    // Remove tutorial param from URL without navigating
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('tutorial');
      window.history.replaceState({}, '', url.toString());
    }
  };

  // Handle next spotlight step
  const handleSpotlightNext = () => {
    setSpotlightStep(prev => prev + 1);
  };

  /**
   * The tour's last step. Hands the detected region to the SAME entry point the
   * globe and the flag tiles use, so the tutorial ENDS by demonstrating the real
   * flow — fly to the country, open its 2D map, pick a province or the whole
   * country — instead of skipping straight to the destination.
   *
   * The commit that eventually follows still carries `tutorial=step2`: every
   * path out of the map funnels through `performCommit`, which passes
   * `tutorialMode` to the shared resolver.
   *
   * `beginCountrySelection` is declared further down this component. That is
   * safe (and already the established pattern here): this closure only reads the
   * binding when the tour finishes, long after the render that initialized it.
   */
  const handleTutorialComplete = () => {
    setShowTutorialSpotlight(false);
    if (detectedRegion) {
      beginCountrySelection(detectedRegion);
    }
  };

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // Group regions by status for better organization (the panel renders
  // available and coming-soon separately)
  const categorizedRegions = getAvailableRegions().reduce(
    (acc, region) => {
      const regionStatus = regionAvailability.find((r) => r.region === region);
      const isAvailable = regionStatus?.isAvailable ?? false;
      const isComingSoon = regionStatus && !regionStatus.isAvailable;

      if (isAvailable) {
        acc.available.push(region);
      } else if (isComingSoon) {
        acc.comingSoon.push(region);
      } else {
        acc.unavailable.push(region);
      }

      return acc;
    },
    { available: [] as string[], comingSoon: [] as string[], unavailable: [] as string[] },
  );

  // Helper function to check if a region is available based on server validation
  const isRegionAvailableWithData = (regionName: string): boolean => {
    const mappedRegionName = getMapRegionName(regionName);
    if (!mappedRegionName) return false;

    // Check both the original isRegionAvailable and server-side validation
    const isBasicallyAvailable = isRegionAvailable(mappedRegionName);
    if (!isBasicallyAvailable) return false;

    // Check server-side data for URL mappings availability
    const regionStatus = regionAvailability.find((r) => r.region === mappedRegionName);
    return regionStatus ? regionStatus.isAvailable : false;
  };

  // Get region status for coming soon display
  const getRegionStatus = (regionName: string): 'available' | 'coming-soon' | 'unavailable' => {
    const mappedRegionName = getMapRegionName(regionName);
    if (!mappedRegionName) return 'unavailable';

    const isBasicallyAvailable = isRegionAvailable(mappedRegionName);
    if (!isBasicallyAvailable) return 'unavailable';

    const regionStatus = regionAvailability.find((r) => r.region === mappedRegionName);
    if (!regionStatus) return 'unavailable';

    return regionStatus.isAvailable ? 'available' : 'coming-soon';
  };

  // Build region colors map for 3D globe - maps geography names to status
  const regionColors = useMemo(() => {
    const colors: { [countryName: string]: 'available' | 'coming-soon' | 'unavailable' } = {};

    // Paint every world-atlas spelling of every region, straight from the
    // shared alias table — no second list to keep in sync.
    for (const region of Object.values(Region)) {
      if (region === Region.All) continue;
      const status = getRegionStatus(region);
      for (const geoName of getRegionGeoNames(region)) {
        colors[geoName] = status;
      }
    }

    return colors;
  }, [regionAvailability]);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMouseX(e.clientX);
    setMouseY(e.clientY);
  };

  /**
   * Internal country code for a Region enum name. The parallel-enum join is the
   * authority; the locale table is the fallback for a region the join misses.
   */
  const countryCodeForRegion = (regionName: string): string | null => {
    const fromEnum = REGION_NAME_TO_CODE[regionName];
    if (fromEnum) return fromEnum;
    const defaultLocale = getDefaultLocaleForRegion(regionName);
    return defaultLocale ? countryLanguages[defaultLocale as Locale]?.regionCode ?? null : null;
  };

  /**
   * Country-only selection: the globe, a flag tile, the tutorial's auto-pick,
   * and any country with no admin-1 map all land here.
   *
   * Epic 4: this used to navigate WITHOUT writing a UserLocation, so picking
   * "Canada" left the header badge and the AI with nothing while picking
   * "Quebec" on the map worked. It now commits through the shared resolver like
   * every other path, with the whole-country sentinel as the subregion.
   */
  const selectWholeCountry = (country: any, source: RegionSelectionSource) => {
    const geoName = country.properties?.name || country;
    const mappedRegionName = getMapRegionName(geoName);

    if (!mappedRegionName || !isRegionAvailableWithData(geoName)) {
      const status = getRegionStatus(geoName);
      if (status === 'coming-soon') {
        console.log(`Region ${geoName} is coming soon - URL mappings not yet available`);
        // Show elegant toast notification instead of alert. The toast is prose,
        // so it gets the visitor's spelling of the country, not the atlas's.
        showComingSoon(localizedGeoName(geoName));
      } else {
        console.log(`Region ${geoName} is not available`);
      }
      return;
    }

    const countryCode = countryCodeForRegion(mappedRegionName);
    if (!countryCode) return;

    commitRegionSelection({
      regionName: mappedRegionName,
      countryCode,
      subregion: ALL_REGIONS_SENTINEL,
      label: getFullRegionName(mappedRegionName),
      coordinates: null,
      source,
    });
  };

  const handleSelectCountry = (country: any) => selectWholeCountry(country, 'globe');

  // Tutorial-aware version. The `tutorial=step2` param and the "ignore
  // returnUrl" rule both come from `tutorialMode` inside the shared resolver,
  // so the only thing that differs here is the recorded source.
  const handleSelectCountryWithTutorial = (country: any) => selectWholeCountry(country, 'tutorial');

  // Use tutorial-aware handler when in tutorial mode
  const handleCountrySelection = tutorialMode ? handleSelectCountryWithTutorial : handleSelectCountry;

  // ─── EPIC 3: globe → animated 2D country map ─────────────────────────────
  const [flyToCountry, setFlyToCountry] = useState<string | null>(null);
  const [view, setView] = useState<RegionView>({ mode: 'globe' });
  const [hoveredSubregion, setHoveredSubregion] = useState<string | null>(null);
  const [focusableCountries, setFocusableCountries] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const isMobile = useMediaQuery(MOBILE_QUERY);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const mapContent = useMemo(() => getRegionMapUiContent(locale), [locale]);

  // Effects and DOM listeners need the CURRENT view without re-subscribing on
  // every transition, and the fly/return callbacks fire outside React's data
  // flow entirely.
  const viewRef = useRef<RegionView>(view);
  viewRef.current = view;
  /** True while the camera is parked on a country and owes us a return flight. */
  const flewRef = useRef(false);
  /** True while we own the extra history entry that browser Back consumes. */
  const pushedHistoryRef = useRef(false);
  /** Root element, so the mobile takeover can sit flush under the real header. */
  const rootRef = useRef<HTMLDivElement>(null);
  /** The right-hand column. Focus lands here when the opener cannot be found. */
  const listPanelRef = useRef<HTMLDivElement>(null);
  /**
   * Whoever opened the country map, so closing it can hand focus back.
   *
   * Both halves are kept: the ELEMENT for a control that survives, and its ID
   * because the usual opener is a country tile inside `LocationSearchPanel`,
   * which unmounts while the map is open and remounts as a different node with
   * the same id. Null means the map was never opened, which is what stops the
   * restore effect from stealing focus on first render.
   */
  const mapTriggerRef = useRef<{ node: HTMLElement | null; id: string } | null>(null);
  const [takeoverTop, setTakeoverTop] = useState(64);

  // The globe speaks world-atlas spellings ("United States of America") and the
  // flag tiles speak Region enum names ("USA"). Resolve through the shared
  // alias table against the names the globe actually indexed, so there is no
  // second hand-kept list to drift.
  const toGlobeCountryName = useCallback(
    (name: string): string => {
      if (focusableCountries.has(name)) return name;
      const region = getMapRegionName(name);
      if (!region) return name;
      return getRegionGeoNames(region).find((alias) => focusableCountries.has(alias)) ?? name;
    },
    [focusableCountries],
  );

  const buildCountryTarget = useCallback(
    (name: string): CountryTarget | null => {
      /* Normalize FIRST. Callers arrive speaking different vocabularies: the
       * flag tiles pass Region enum names ("USA"), the globe passes world-atlas
       * spellings ("United States of America"). REGION_NAME_TO_CODE is keyed by
       * Region names ONLY, so an atlas name missed, this returned null, and
       * `beginCountrySelection` fell through to a whole-country commit — the
       * visitor got sent straight to nationwide results with no state picker.
       *
       * It bit exactly the USA and the UK, because they are the only two whose
       * Region value is an abbreviation rather than the atlas spelling; every
       * other country's enum value happens to equal its atlas name, which is
       * why this looked like it worked. `getRegionStatus` already normalizes
       * the same way, which is why the click was accepted at all.
       */
      const regionName = toRegionName(name);
      const countryCode = countryCodeForAnyName(name);
      if (!countryCode) return null;
      return {
        regionName,
        countryCode,
        globeName: toGlobeCountryName(regionName),
        displayName: getFullRegionName(regionName),
      };
    },
    [toGlobeCountryName, getFullRegionName],
  );

  const openCountry = useCallback(
    (target: CountryTarget, preselect: string | null, pending: PendingPlace | null) => {
      setHoveredSubregion(null);
      // Recorded BEFORE anything unmounts. A globe click leaves this on <body>
      // or the canvas, neither of which can take focus back — the restore falls
      // through to the panel for those, which is the point of having a
      // fallback.
      const active =
        typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      mapTriggerRef.current = { node: active, id: active?.id ?? '' };
      // The ~900ms flight is exactly the budget the geometry needs.
      prefetchCountryMap(target.countryCode);
      // Mobile skips the flight: the globe sits under the list panel there and is
      // frequently scrolled off screen, so the camera move would be invisible.
      if (isMobile) {
        setView({ mode: 'country', country: target, preselect, pending });
        return;
      }
      flewRef.current = true;
      setFlyToCountry(target.globeName);
      setView({ mode: 'flying', country: target, preselect, pending });
    },
    [isMobile],
  );

  const beginCountrySelection = (name: string) => {
    // Coming-soon regions keep the immediate toast: they never navigate, so
    // flying there would park the camera with nothing to return it.
    //
    // Epic 10: the tutorial used to short-circuit to a country-level commit
    // here, which meant the ONE context that narrates this page ("click your
    // country, then narrow down to your province") was also the one context
    // that never showed the map it was describing. It now takes the same path
    // as everyone else; `tutorial=step2` rides along on whichever commit the
    // visitor ends up making, because every path funnels through performCommit.
    if (getRegionStatus(name) !== 'available') {
      handleCountrySelection(name);
      return;
    }
    const target = buildCountryTarget(name);
    if (!target || !hasCountryMap(target.countryCode)) {
      // No admin-1 geometry for this country — keep today's direct redirect
      // rather than opening an empty map.
      handleCountrySelection(name);
      return;
    }
    openCountry(target, null, null);
  };

  /** Globe arrival. Ignores a stale arrival for a country we already left. */
  const handleFlyComplete = useCallback((countryName: string) => {
    setView((prev) =>
      prev.mode === 'flying' && prev.country.globeName === countryName
        ? { mode: 'country', country: prev.country, preselect: prev.preselect, pending: prev.pending }
        : prev,
    );
  }, []);

  const handleReturnComplete = useCallback(() => {
    setView((prev) => (prev.mode === 'closing' ? { mode: 'globe' } : prev));
  }, []);

  /** Start closing. Called by the Back button, Escape and popstate alike. */
  const closeCountry = useCallback(() => {
    const current = viewRef.current;
    if (current.mode !== 'flying' && current.mode !== 'country') return;
    setHoveredSubregion(null);
    setView({ mode: 'closing', country: current.country });
    // Nothing is mounted to fade out mid-flight, so hand straight over to the
    // return flight (or to the globe, when we never flew).
    if (current.mode === 'flying') {
      if (flewRef.current) {
        flewRef.current = false;
        setFlyToCountry(null);
      } else {
        setView({ mode: 'globe' });
      }
    }
  }, []);

  /**
   * The map has finished fading out. Only now is the return flight worth
   * playing — clearing focusCountry any earlier animates the camera behind an
   * opaque overlay.
   */
  const handleMapExitComplete = useCallback(() => {
    if (viewRef.current.mode !== 'closing') return;
    if (flewRef.current) {
      flewRef.current = false;
      setFlyToCountry(null);
    } else {
      setView({ mode: 'globe' });
    }
  }, []);

  /**
   * Leave /region entirely. Only reachable from the globe view — with a country
   * open the same button closes the map first, so back always costs exactly one
   * step. Falls back to the locale home when there is no returnUrl, the same
   * contract BasicContainer's button had.
   */
  const leaveRegionPage = useCallback(() => {
    router.push(returnUrl || `/${locale.path}`);
  }, [router, returnUrl, locale.path]);

  /** Back button / Escape: spend our history entry so the URL stays honest. */
  const requestClose = useCallback(() => {
    if (pushedHistoryRef.current && typeof window !== 'undefined') {
      pushedHistoryRef.current = false;
      window.history.back(); // popstate below finishes the close
      return;
    }
    closeCountry();
  }, [closeCountry]);

  const countryOpen = view.mode === 'flying' || view.mode === 'country';

  /**
   * Hand focus back when the map closes.
   *
   * Escape, the Back pill and browser Back all end in `closeCountry`, which
   * unmounts whatever was focused; nothing else in this file moves focus, so it
   * falls to `<body>` and a keyboard user loses their place entirely. The
   * easter-egg dialog already does this correctly on unmount
   * (`subregion-prompt-dialog.tsx`) — this is the same contract for a surface
   * that is not a self-contained dialog component.
   *
   * WHY IT RETRIES. The opener is normally a country tile in
   * `LocationSearchPanel`, and that panel is behind an `AnimatePresence
   * mode="wait"` crossfade: the moment the map closes the tile does not exist
   * yet, and will not for another ~180ms. So this re-looks for it by id for a
   * short while and only then settles for the panel itself, rather than
   * flashing focus somewhere and moving it again.
   */
  useEffect(() => {
    if (countryOpen) return;
    const trigger = mapTriggerRef.current;
    if (!trigger) return;
    mapTriggerRef.current = null;

    /** True only if the element actually TOOK focus — a canvas will not. */
    const tryFocus = (element: HTMLElement | null): boolean => {
      if (!element || !element.isConnected) return false;
      element.focus({ preventScroll: true });
      return document.activeElement === element;
    };

    let frames = 0;
    let raf = 0;
    const attempt = (): void => {
      if (trigger.id && tryFocus(document.getElementById(trigger.id))) return;
      if (tryFocus(trigger.node)) return;
      if (frames++ < FOCUS_RESTORE_FRAMES) {
        raf = requestAnimationFrame(attempt);
        return;
      }
      tryFocus(listPanelRef.current);
    };
    attempt();

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [countryOpen]);

  // Pin the mobile takeover to the real top of the content area (the header is
  // a few px shorter than the 4rem the page's height math assumes).
  useEffect(() => {
    if (!countryOpen || !isMobile) return;
    const read = () => {
      const top = rootRef.current?.getBoundingClientRect().top;
      if (typeof top === 'number') setTakeoverTop(Math.max(0, Math.round(top)));
    };
    read();
    window.addEventListener('resize', read);
    return () => window.removeEventListener('resize', read);
  }, [countryOpen, isMobile]);

  // One pushState per country opening, so the hardware/browser Back button
  // closes the map instead of leaving the page.
  useEffect(() => {
    if (!countryOpen || pushedHistoryRef.current || typeof window === 'undefined') return;
    pushedHistoryRef.current = true;
    window.history.pushState({ regionCountryMap: true }, '', window.location.href);
  }, [countryOpen]);

  useEffect(() => {
    if (!countryOpen) return;
    const onPopState = () => {
      pushedHistoryRef.current = false;
      closeCountry();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      requestClose();
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [countryOpen, closeCountry, requestClose]);

  /**
   * THE commit. The ONLY place in this file that writes a location and
   * navigates; it delegates to the shared resolver (`@/lib/region-selection`)
   * so the /region page and everything outside it commit identically.
   *
   * Nothing calls this directly except `commitRegionSelection` below and the
   * easter-egg dialog's answer handler — the two branches of one decision, so
   * a selection produces exactly one commit and exactly one navigation.
   */
  const performCommit = useCallback(
    ({ regionName, selection }: PendingSelection) => {
      // A commit navigates away, so there is no place left to hand focus back
      // to. Dropping the trigger keeps the restore effect out of the way of a
      // page that is leaving.
      mapTriggerRef.current = null;
      setSelectedCountry({ id: regionName, name: regionName });
      commitSelection(selection, {
        regionName,
        returnUrl,
        searchQuery: initialSearchQuery,
        tutorialMode,
        isDev: process.env.DEV_MODE === 'true' || process.env.NODE_ENV === 'development',
        push: (url) => router.push(url),
      });
    },
    [initialSearchQuery, returnUrl, router, tutorialMode],
  );

  // ─── EPIC 6: the region scope prompt ─────────────────────────────────────
  //
  // A selection that lands on a province or a state gets one question before it
  // commits: this place first, or the whole country? Quebec is asked with the
  // wink; everywhere else gets the neutral confirmation that leads with the
  // nationwide shelf, because narrowing to one region narrows the catalog. The
  // dialog then decides what is committed, which is why the interception has to
  // happen HERE rather than after the fact — a commit followed by a correction
  // would be two stores, two `loc` params and two navigations.

  const [subregionPrompt, setSubregionPrompt] = useState<SubregionPrompt | null>(null);
  /**
   * Mirror of the state above. The answer handler must be single-shot even when
   * two events land in one batch (Escape while a click is resolving), and state
   * that has not re-rendered yet cannot prove that.
   */
  const subregionPromptRef = useRef<SubregionPrompt | null>(null);
  /**
   * Monotonic ticket for the eligibility check. A second selection while the
   * first is still asking the server supersedes it: the stale answer is
   * dropped, so two fast clicks produce one dialog and one commit, not two.
   */
  const promptGateRef = useRef(0);

  // Pre-warm: resolve THIS visitor's own place (stored location → `loc` handoff
  // → IP) and cache the server's answer, so the check that runs on their click
  // resolves from memory and the dialog lands with the click instead of after
  // it. Skips entirely — no detection, no fetch — once they have answered.
  const homeEligibility = useSubregionEligibility(locale.regionCode, locale.languageCode);
  const homeFlagSrc = homeEligibility.subregion?.flagSrc ?? null;

  useEffect(() => {
    // …and the flag with it, so the card does not open on a blank box. A 1KB
    // SVG, and only the one this visitor's own place would actually show:
    // Québec's for the wink, their country's for the standard confirmation.
    if (!homeFlagSrc || typeof window === 'undefined') return;
    new window.Image().src = homeFlagSrc;
  }, [homeFlagSrc]);

  /**
   * Cheap, synchronous "is this even a candidate?" — no I/O, no storage.
   *
   * The tutorial stays excluded even though it now produces real `map-click`
   * selections, and deliberately so: answering this question is a ONE-TIME event
   * (`markSubregionPromptAnswered` burns it for the country permanently), so
   * letting it fire during a guided tour would spend a visitor's only real
   * prompt on a demo — and stack a second modal on top of the tour's own.
   */
  const isSubregionPromptCandidate = useCallback(
    (selection: RegionSelection): boolean =>
      !tutorialMode &&
      SUBREGION_PROMPT_SOURCES.has(selection.source) &&
      !isWholeCountry(selection.subregion),
    [tutorialMode],
  );

  /**
   * THE funnel. Every selection path in this file still calls exactly this, and
   * it ends in exactly one of two places: a commit now, or a dialog that will
   * commit once. Never both.
   */
  const commitRegionSelection = useCallback(
    (input: {
      regionName: string;
      countryCode: string;
      /** Subregion code, or ALL_REGIONS_SENTINEL for the whole country. */
      subregion: string;
      /** Display label for the stored location (city, else area, else country). */
      label: string;
      coordinates?: { lat: number; lng: number } | null;
      source: RegionSelectionSource;
    }) => {
      const pending: PendingSelection = {
        regionName: input.regionName,
        selection: {
          countryCode: input.countryCode,
          subregion: input.subregion,
          city: input.label,
          coordinates: input.coordinates,
          source: input.source,
        },
      };

      // Every selection takes a ticket, not just the ones that ask a question:
      // a whole-country pick made while a subregion check is still in flight
      // must also invalidate it, or the stale answer commits on top of it.
      const ticket = ++promptGateRef.current;

      if (!isSubregionPromptCandidate(pending.selection)) {
        performCommit(pending);
        return;
      }

      void (async () => {
        // Both one-time skips and the server gate live in here; null means
        // "nothing to ask", which is the overwhelmingly common answer.
        const feature = await resolveSubregionPromptTarget(
          pending.selection.countryCode,
          pending.selection.subregion,
          locale.languageCode,
        );
        // Superseded by a newer selection — that one owns the commit now.
        if (ticket !== promptGateRef.current) return;
        if (!feature) {
          performCommit(pending);
          return;
        }
        subregionPromptRef.current = { pending, feature };
        setSubregionPrompt(subregionPromptRef.current);
      })();
    },
    [isSubregionPromptCandidate, locale.languageCode, performCommit],
  );

  /** The answer. Single-shot, then the commit the shopper actually asked for. */
  const handleSubregionPromptChoice = useCallback(
    (choice: SubregionPromptChoice) => {
      const current = subregionPromptRef.current;
      if (!current) return; // Already answered — a duplicate event, ignore it.
      subregionPromptRef.current = null;
      setSubregionPrompt(null);

      // Written before the commit: the commit navigates, and on a cross-origin
      // hop this page stops executing the moment it does.
      markSubregionPromptAnswered(current.pending.selection.countryCode, choice);
      performCommit({
        regionName: current.pending.regionName,
        selection: applySubregionPromptChoice(current.pending.selection, choice, current.feature),
      });
    },
    [performCommit],
  );

  const activeCountry =
    view.mode === 'flying' || view.mode === 'country' || view.mode === 'closing' ? view.country : null;
  const activePreselect = view.mode === 'flying' || view.mode === 'country' ? view.preselect : null;
  const activePending = view.mode === 'flying' || view.mode === 'country' ? view.pending : null;
  // The map only mounts on arrival ('country'), never during the flight — the
  // scale-down entrance has to continue a zoom that has already finished.
  const desktopMapOpen = !isMobile && view.mode === 'country';
  const mobileMapOpen = isMobile && view.mode === 'country';
  /**
   * WHO renders the subdivision list — at most one surface, ever.
   *
   * Asked once, as a single value, rather than derived independently at the two
   * mount sites: both used to say yes on a phone, which put two complete,
   * tabbable copies of all 13 provinces on the page. See
   * `@/lib/region-view-surface`, where the rule is unit-tested.
   */
  const listSurface = subdivisionListSurface(view.mode, isMobile);

  const handleSubregionSelect = useCallback(
    (code: string, name: string) => {
      const current = viewRef.current;
      if (current.mode !== 'country' && current.mode !== 'flying') return;
      const pending = current.pending;
      // Coordinates only survive onto the province they actually fall in.
      const keepCoords =
        pending && pending.subregionCode === code && pending.lat !== null && pending.lng !== null;
      commitRegionSelection({
        regionName: current.country.regionName,
        countryCode: current.country.countryCode,
        subregion: code,
        label: keepCoords && pending?.city ? pending.city : name,
        coordinates: keepCoords ? { lat: pending.lat as number, lng: pending.lng as number } : null,
        source: 'map-click',
      });
    },
    [commitRegionSelection],
  );

  const handleWholeCountrySelect = useCallback(() => {
    const current = viewRef.current;
    if (current.mode !== 'country' && current.mode !== 'flying') return;
    const pending = current.pending;
    commitRegionSelection({
      regionName: current.country.regionName,
      countryCode: current.country.countryCode,
      subregion: ALL_REGIONS_SENTINEL,
      label: pending?.city || current.country.displayName,
      coordinates:
        pending && pending.lat !== null && pending.lng !== null
          ? { lat: pending.lat, lng: pending.lng }
          : null,
      source: 'map-click',
    });
  }, [commitRegionSelection]);

  /** "Use {city}, {province}" — take the resolution exactly as Google gave it. */
  const handleConfirmPending = useCallback(() => {
    const current = viewRef.current;
    if (current.mode !== 'country' && current.mode !== 'flying') return;
    const pending = current.pending;
    if (!pending) return;
    commitRegionSelection({
      regionName: current.country.regionName,
      countryCode: current.country.countryCode,
      subregion: pending.subregionCode ?? ALL_REGIONS_SENTINEL,
      label: pending.city || current.country.displayName,
      coordinates:
        pending.lat !== null && pending.lng !== null ? { lat: pending.lat, lng: pending.lng } : null,
      source: 'places',
    });
  }, [commitRegionSelection]);
  // ─── end Epic 3 state machine ────────────────────────────────────────────

  // A Google-resolved place (or the IP-detected location): persist the precise
  // location for hyper-localized results, then route into the right region site.
  const handleResolvedPlace = (place: PanelResolvedPlace) => {
    const regionName = place.regionCode ? getRegionForCountryCode(place.regionCode) : null;
    if (!regionName) {
      // Country we don't serve (yet) — surface the same coming-soon toast
      showComingSoon(place.label || place.countryIso || place.regionCode || 'this area');
      return;
    }
    const status = getRegionStatus(regionName);
    if (status !== 'available') {
      showComingSoon(getFullRegionName(regionName));
      return;
    }

    const countryCode = place.regionCode as string;
    // Normalize the admin area to an internal region code (QC/NY style) where a
    // curated map exists; otherwise keep Google's short code so AI prompts
    // still receive real signal for countries without a region map.
    const admin1 = place.admin1Long || place.admin1Short || null;
    // The admin-1 registry is the canonical resolver (it absorbs Google's
    // code-vs-name duality and the localized spellings); the legacy chain stays
    // as the fallback for countries the registry has no data for.
    const registryCode = admin1 ? getSubregion(countryCode, admin1)?.code ?? null : null;
    const internalRegion =
      registryCode ||
      (admin1 ? findRegionCodeByName(admin1, countryCode) : null) ||
      (place.admin1Short && getRegionMapping(place.admin1Short) ? place.admin1Short : null) ||
      place.admin1Short ||
      ALL_REGIONS_SENTINEL;

    // Epic 3: a resolved place no longer commits on the spot. Fly to the
    // country and open the map with the province preselected and a pin on the
    // coordinates, so the visitor confirms (or overrides) what we inferred.
    const target = buildCountryTarget(regionName);
    // Epic 10: the tutorial takes this branch too now — a searched address
    // should land on the map with its province preselected during the tour
    // exactly as it does outside it.
    if (target && hasCountryMap(target.countryCode)) {
      openCountry(target, registryCode, {
        city: place.city || null,
        subregionCode: registryCode,
        lat: typeof place.lat === 'number' ? place.lat : null,
        lng: typeof place.lng === 'number' ? place.lng : null,
      });
      return;
    }

    // No admin-1 map for this country: commit the resolved place straight
    // through the shared resolver.
    commitRegionSelection({
      regionName,
      countryCode,
      subregion: internalRegion,
      label: place.city || admin1 || getFullRegionName(regionName),
      coordinates:
        typeof place.lat === 'number' && typeof place.lng === 'number'
          ? { lat: place.lat, lng: place.lng }
          : null,
      source: 'places',
    });
  };

  // w-full (NOT w-screen): 100vw includes the vertical scrollbar width, so
  // w-screen intermittently overflowed by the scrollbar and produced a
  // horizontal scroll. overflow-x-clip guards against edge-hugging absolute
  // children (hover tooltip) ever re-introducing it.
  //
  // No family class here on purpose. This element carried `font-sans` (Tailwind's
  // default stack, not even the brand's), which pinned the entire region page to
  // the sans and made it the one surface the `serif-typography` flag could not
  // reach. The page voice belongs to <html>; the map's own labels opt out
  // individually with `--font-ui` (country-map-view.tsx).
  return (
    <div
      ref={rootRef}
      onMouseMove={handleMouseMove}
      className="relative h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)] w-full max-w-full overflow-x-clip z-10"
    >
      {/* Tutorial Welcome Modal - first step of tutorial */}
      {showTutorialWelcome && (
        <TutorialWelcome
          locale={locale}
          onStart={handleWelcomeStart}
          onSkip={handleTutorialSkip}
        />
      )}

      {/* Tutorial Spotlight - guided tour with callout boxes */}
      {showTutorialSpotlight && (
        <TutorialSpotlight
          locale={locale}
          targets={spotlightTargets}
          currentStep={spotlightStep}
          onNext={handleSpotlightNext}
          onSkip={handleTutorialSkip}
          onComplete={handleTutorialComplete}
        />
      )}


      {hoveredCountry && !countryOpen && view.mode === 'globe' && (
        <div
          className="pointer-events-none absolute z-20 rounded border bg-light bg-opacity-95 px-3 py-1 text-sm shadow-lg dark:border-light dark:bg-dark"
          style={{ top: mouseY - 30, left: mouseX + 10 }}
        >
          <div>{localizedGeoName(hoveredCountry)}</div>
          {(() => {
            // Status still keys on the RAW atlas spelling — only the label above
            // is localized.
            const status = getRegionStatus(hoveredCountry);
            const statusConfig = {
              available: { text: uiContent.availableTooltip, color: 'text-green-600' },
              'coming-soon': { text: uiContent.locationPanel.comingSoon, color: 'text-yellow-600' },
              unavailable: { text: uiContent.notAvailableTooltip, color: 'text-gray-500' },
            };
            const config = statusConfig[status];
            return <div className={`text-xs ${config.color}`}>{config.text}</div>;
          })()}
        </div>
      )}

      <div className="flex h-full w-full min-w-0 max-md:flex-col">
        {/* 3D Globe View — min-w-0/min-h-0 so the canvas can never force the
            flex row wider than the viewport */}
        <div id="region-globe-container" className="relative flex-1 min-w-0 min-h-0 w-full h-full max-md:order-2">
          {/* The canvas fades under the 2D map rather than unmounting: the
              camera must stay parked on the country so the return flight has
              somewhere to fly back from. */}
          <motion.div
            className="h-full w-full"
            animate={{ opacity: desktopMapOpen ? 0 : 1 }}
            transition={prefersReducedMotion ? { duration: 0.2 } : MAP_TRANSITION}
            style={{ pointerEvents: desktopMapOpen ? 'none' : 'auto' }}
          >
            <Suspense fallback={
              <div className="flex items-center justify-center h-full w-full bg-[#d4e9f7]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00a798] mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">{mapContent.globeLoading}</p>
                </div>
              </div>
            }>
              <InteractiveRegionGlobe
                className="w-full h-full"
                speed={0.5}
                interactive={true}
                autoRotate={true}
                regionColors={regionColors}
                onCountryClick={(countryName) => {
                  const status = getRegionStatus(countryName);
                  if (status === 'available' || status === 'coming-soon') {
                    beginCountrySelection(countryName);
                  }
                }}
                onCountryHover={(countryName) => setHoveredCountry(countryName)}
                selectedCountry={selectedCountry?.name || null}
                isDark={isDark}
                /* Epic 3 state machine — see openCountry / closeCountry above.
                   Always on: this also populates the focus index that
                   `toGlobeCountryName` resolves against, so disabling it in the
                   tutorial (as Epic 3 did) left buildCountryTarget guessing at
                   atlas spellings as well as killing the flight. */
                flightsEnabled
                focusCountry={flyToCountry}
                onFlyComplete={handleFlyComplete}
                onReturnComplete={handleReturnComplete}
                onFocusIndexReady={setFocusableCountries}
              />
            </Suspense>

            {/* Instructions overlay for 3D globe - hidden on mobile */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 max-md:hidden">
              <div className="rounded-full bg-white/90 px-6 py-3 shadow-lg backdrop-blur-sm dark:bg-gray-800/90">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <svg className="h-5 w-5 text-[#00a798]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                  {mapContent.globeHint}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Desktop: the map opens INSIDE the globe frame. Entering at 1.06
              and settling to 1.0 continues the camera's zoom, so the swap from
              3D to 2D reads as one move instead of a cut. */}
          <AnimatePresence onExitComplete={handleMapExitComplete}>
            {desktopMapOpen && activeCountry && (
              <motion.div
                key={`map-${activeCountry.countryCode}`}
                className="absolute inset-0 z-20"
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 1.06 }}
                animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 1.04 }}
                transition={prefersReducedMotion ? { duration: 0.2 } : MAP_TRANSITION}
              >
                <CountryMapView
                  countryCode={activeCountry.countryCode}
                  countryName={activeCountry.displayName}
                  lang={locale.languageCode}
                  content={mapContent}
                  onSelectSubregion={handleSubregionSelect}
                  onSelectWholeCountry={handleWholeCountrySelect}
                  preselect={activePreselect}
                  hoveredCode={hoveredSubregion}
                  onHoverChange={setHoveredSubregion}
                  pin={
                    activePending && activePending.lat !== null && activePending.lng !== null
                      ? { lat: activePending.lat, lng: activePending.lng, label: activePending.city }
                      : null
                  }
                  /* Desktop only (this branch is gated on !isMobile). The panel's
                     card sits in the far column, ~400px from where the eye lands
                     after the flight — the pill puts the same escape hatch on the
                     map itself. The mobile takeover below deliberately omits it:
                     there the sheet's card is directly under a 38vh map. */
                  showAllCountryAction
                  reducedMotion={prefersReducedMotion}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* The ONLY back control on this page (the page suppresses
              BasicContainer's fixed top-left one). It sits outside the map's
              AnimatePresence so it survives the 2D/3D swap, and it steps back
              one level at a time: country map -> globe -> wherever the visitor
              came from. Top-RIGHT because the top-left corner belongs to the
              header logo. */}
          <button
            type="button"
            onClick={countryOpen ? requestClose : leaveRegionPage}
            aria-label={mapContent.back}
            className="absolute right-3 top-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-2 text-xs font-semibold text-gray-800 shadow-lg backdrop-blur transition-colors hover:bg-white dark:bg-card/90 dark:text-ink dark:hover:bg-card"
          >
            <FiArrowLeft className="h-3.5 w-3.5" />
            {mapContent.back}
          </button>
        </div>

        {/* Reworked Region Selection Panel — Google-powered location search.
            flex-none + fixed md widths keep globe(flex-1) + panel summing to
            exactly the container width (no horizontal overflow). On mobile the
            panel comes first so search is immediately reachable. */}
        <div
          id="region-list-panel"
          ref={listPanelRef}
          /* Programmatic focus only — never in the tab order. It is the last
             resort of the close-focus restore, for openers that cannot take
             focus back (a globe click leaves it on the canvas). */
          tabIndex={-1}
          className="flex flex-none flex-col backdrop-blur outline-none max-md:order-1 max-md:max-h-[45%] md:h-full md:w-80 md:border-l md:border-gray-200 dark:md:border-gray-700 lg:w-96"
        >
          {/* The panel crossfades the moment the camera leaves, so by the time
              the map lands the choice is already on screen.

              `listSurface` — not `countryOpen` — is what gates this. Below
              768px the list belongs to the full-screen takeover further down,
              and this column stays mounted underneath it, so gating on
              "a country is open" put TWO complete, visible, tabbable copies of
              all 13 provinces on a phone. Conditional rather than CSS-hidden:
              `display:none` would fix the tab order and still leave two
              `[data-subregion]` sets in the DOM for the next person to trip
              over. */}
          <AnimatePresence mode="wait" initial={false}>
            {listSurface === 'panel' && activeCountry ? (
              <motion.div
                key="subdivision-panel"
                className="flex h-full min-h-0 flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <SubdivisionPanel
                  countryCode={activeCountry.countryCode}
                  countryName={activeCountry.displayName}
                  lang={locale.languageCode}
                  content={mapContent}
                  onSelectSubregion={handleSubregionSelect}
                  onSelectWholeCountry={handleWholeCountrySelect}
                  onBack={requestClose}
                  selectedCode={activePreselect}
                  hoveredCode={hoveredSubregion}
                  onHoverChange={setHoveredSubregion}
                  confirm={
                    activePending && activePending.city
                      ? {
                          city: activePending.city,
                          areaName: activePending.subregionCode
                            ? getSubregion(activeCountry.countryCode, activePending.subregionCode)?.name ?? null
                            : null,
                        }
                      : null
                  }
                  onConfirm={handleConfirmPending}
                />
              </motion.div>
            ) : (
              <motion.div
                key="location-search-panel"
                className="flex h-full min-h-0 flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <LocationSearchPanel
                  locale={locale}
                  available={categorizedRegions.available}
                  comingSoon={categorizedRegions.comingSoon}
                  selectedCountry={selectedCountry?.name || null}
                  getFullRegionName={getFullRegionName}
                  onCountrySelect={beginCountrySelection}
                  onPlaceResolved={handleResolvedPlace}
                  uiContent={uiContent}
                />
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>

      {/* Mobile: a full-screen takeover, not an in-container overlay. The globe
          lives under the list panel on `max-md`, so an inset overlay would be a
          cramped strip. Map on top at a definite height, list scrolling
          beneath, "All of {country}" pinned in the list head. */}
      <AnimatePresence onExitComplete={handleMapExitComplete}>
        {mobileMapOpen && activeCountry && (
          <motion.div
            key={`takeover-${activeCountry.countryCode}`}
            /* `top` is MEASURED, not `top-16`: the page assumes a 4rem header
               in its own h-[calc(100vh-4rem)], but the rendered header is a
               few px shorter on mobile and the mismatch shows as a seam. */
            style={{ top: takeoverTop }}
            className="fixed inset-x-0 bottom-0 z-50 flex flex-col bg-white dark:bg-dark"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
            transition={prefersReducedMotion ? { duration: 0.2 } : { duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative h-[38vh] flex-none">
              <CountryMapView
                countryCode={activeCountry.countryCode}
                countryName={activeCountry.displayName}
                lang={locale.languageCode}
                content={mapContent}
                onSelectSubregion={handleSubregionSelect}
                onSelectWholeCountry={handleWholeCountrySelect}
                preselect={activePreselect}
                hoveredCode={hoveredSubregion}
                onHoverChange={setHoveredSubregion}
                pin={
                  activePending && activePending.lat !== null && activePending.lng !== null
                    ? { lat: activePending.lat, lng: activePending.lng, label: activePending.city }
                    : null
                }
                reducedMotion={prefersReducedMotion}
              />
              {/* Top-RIGHT here too: BasicContainer's "Go home" pill is
                  fixed at z-[110] over the top-left corner and outranks this
                  overlay's stacking context. */}
              <button
                type="button"
                onClick={requestClose}
                aria-label={mapContent.back}
                className="absolute right-3 top-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-2 text-xs font-semibold text-gray-800 shadow-lg backdrop-blur dark:bg-card/90 dark:text-ink"
              >
                <FiArrowLeft className="h-3.5 w-3.5" />
                {mapContent.back}
              </button>
            </div>
            {/* The other branch of the same single decision — `listSurface`
                answers 'sheet' exactly when the column above answered null. */}
            <div className="min-h-0 flex-1">
              <SubdivisionPanel
                variant="sheet"
                countryCode={activeCountry.countryCode}
                countryName={activeCountry.displayName}
                lang={locale.languageCode}
                content={mapContent}
                onSelectSubregion={handleSubregionSelect}
                onSelectWholeCountry={handleWholeCountrySelect}
                onBack={requestClose}
                selectedCode={activePreselect}
                hoveredCode={hoveredSubregion}
                onHoverChange={setHoveredSubregion}
                confirm={
                  activePending && activePending.city
                    ? {
                        city: activePending.city,
                        areaName: activePending.subregionCode
                          ? getSubregion(activeCountry.countryCode, activePending.subregionCode)?.name ?? null
                          : null,
                      }
                    : null
                }
                onConfirm={handleConfirmPending}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Epic 6 — the subregion easter egg. Fixed-position and modal, so it
          sits outside both map surfaces rather than inside whichever one
          happens to be mounted, and blocks a second selection while it is
          open. AnimatePresence lets it leave gracefully; the commit has
          already happened by then and does not wait for the exit. */}
      <AnimatePresence>
        {subregionPrompt && (
          <SubregionPromptDialog
            key="subregion-prompt"
            feature={subregionPrompt.feature}
            countryName={localizedCountryName(
              subregionPrompt.pending.selection.countryCode,
              locale.languageCode,
            )}
            content={mapContent}
            layout={isMobile ? 'sheet' : 'popover'}
            reducedMotion={prefersReducedMotion}
            onChoose={handleSubregionPromptChoice}
          />
        )}
      </AnimatePresence>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default MapViewer;
